import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useLoad, PageHead, Card, Tabs, BarChart, HBars, Skeleton, ErrorBox, Btn, num, money, exportCsv } from "../ui";
import { absUrl, siteLink } from "../api";

const short = (d) => {
  const [, m, day] = d.split("-");
  return `${Number(day)}/${Number(m)}`;
};

const METRICS = [
  ["views", "Blog views", "#06b6d4", num],
  ["impressions", "Impressions", "#3b82f6", num],
  ["users", "नए users", "#8b5cf6", num],
  ["comments", "Comments", "#10b981", num],
  ["revenue", "Revenue", "#f59e0b", money],
];

export default function Analytics() {
  const [days, setDays] = useState(30);
  const [metric, setMetric] = useState("views");
  const { data, loading, error, reload } = useLoad("analytics", { days }, [days]);
  const m = METRICS.find((x) => x[0] === metric);

  return (
    <div>
      <PageHead icon="fa-solid fa-chart-column" title="Analytics" subtitle="वेबसाइट पर क्या चल रहा है — views, लोग, कमाई"
        actions={
          <>
            <Tabs tabs={[[7, "7 दिन"], [30, "30 दिन"], [90, "90 दिन"]]} value={days} onChange={setDays} />
            <Btn icon="fa-solid fa-download" disabled={!data} onClick={() => exportCsv(data.days, "ycnp-analytics")}>CSV</Btn>
          </>
        } />
      <ErrorBox msg={error} retry={reload} />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        {METRICS.map(([k, l, c, f]) => {
          const total = data ? data.days.reduce((s, d) => s + d[k], 0) : 0;
          const on = metric === k;
          return (
            <button key={k} type="button" onClick={() => setMetric(k)} className="wa-card p-4 text-left transition-transform hover:-translate-y-0.5" style={on ? { borderColor: c, boxShadow: `0 10px 26px -16px ${c}` } : {}}>
              <p className="text-[12px] font-semibold wa-muted flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: c }} />{l}</p>
              <p className="text-2xl font-extrabold mt-1 tabular-nums">{loading ? "…" : f(total)}</p>
            </button>
          );
        })}
      </div>

      <Card title={`${m[1]} — रोज़ का`} subtitle={`पिछले ${days} दिन`} icon="fa-solid fa-chart-simple" className="mb-5">
        {loading ? <Skeleton rows={5} h={36} /> : data && <BarChart height={240} color={m[2]} format={m[3]} label={m[1]} data={data.days.map((d) => ({ label: d.day, short: short(d.day), value: d[metric] }))} />}
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 mb-5">
        <Card title="Traffic कहाँ से आया" subtitle="Google, WhatsApp, Direct…" icon="fa-solid fa-route">
          {loading ? <Skeleton rows={5} h={28} /> : data && <HBars rows={data.sources.map((s) => ({ label: s.source || "direct", value: s.count }))} />}
        </Card>
        <Card title="Top blogs" icon="fa-solid fa-fire" className="xl:col-span-2">
          {loading ? <Skeleton rows={6} h={32} /> : data && (
            <div className="overflow-x-auto -mx-5">
              <table className="wa-tbl">
                <thead><tr><th>#</th><th>Blog</th><th>Author</th><th className="text-right">Views</th><th className="text-right">Likes</th><th className="text-right">Comments</th></tr></thead>
                <tbody>
                  {data.topBlogs.map((b, i) => (
                    <tr key={b.id}>
                      <td className="wa-muted tabular-nums">{i + 1}</td>
                      <td className="max-w-[320px]"><Link to={`/website/blogs/${b.id}`} className="font-semibold hover:underline line-clamp-1">{b.title}</Link></td>
                      <td className="wa-muted">{b.author}</td>
                      <td className="text-right tabular-nums font-semibold">{num(b.views)}</td>
                      <td className="text-right tabular-nums">{num(b.likes)}</td>
                      <td className="text-right tabular-nums">{num(b.comments)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <Card title="Top writers" icon="fa-solid fa-feather">
          {loading ? <Skeleton rows={5} h={28} /> : data && <HBars color="#8b5cf6" rows={data.topAuthors.map((a) => ({ label: `${a.name} (${a.blogs} blogs)`, value: a.views }))} />}
        </Card>
        <Card title="Top posters" icon="fa-regular fa-images">
          {loading ? <Skeleton rows={5} h={28} /> : data && (
            <ul className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {data.topPosters.map((p) => (
                <li key={p.id}>
                  <a href={siteLink(`/posters/${p.slug}`)} target="_blank" rel="noreferrer" className="block group">
                    <img src={absUrl(p.image)} alt="" className="w-full aspect-square object-cover rounded-xl border wa-line group-hover:opacity-90" />
                    <p className="text-[12px] font-semibold mt-1 truncate">{p.title}</p>
                    <p className="text-[11px] wa-muted">{num(p.views)} views · {num(p.likes)} ♥</p>
                  </a>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
